import { Fragment, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import FomoFiLogo from "@/components/brand/FomoFiLogo";
import MarketingMobileNav from "@/components/layout/MarketingMobileNav";
import BackToTop from "@/components/layout/BackToTop";
import MarketingFooter from "@/components/layout/MarketingFooter";
import SectionLink from "@/components/layout/SectionLink";
import { HERO_BLUE } from "@/lib/marketing";
import { WALKTHROUGH_NAV_COPY } from "@fomofi/shared";
import { HowItWorksButton } from "@/components/walkthrough/HowItWorksButton";
import { navKey, type MarketingNavLink } from "@/lib/marketingNav";
import { cn } from "@/lib/utils";

/**
 * Shared shell for the public marketing pages (landing, /features).
 *
 * Header and footer were inline in Landing.tsx until /features needed the same
 * chrome; this is a lift-and-shift of that markup, not a redesign. Two rules
 * keep the landing byte-identical through the move:
 *
 *  1. The layout adds NO vertical padding of its own. The header is `fixed`, so
 *     the first section on each page owns the offset that clears it (the
 *     landing hero's `pt-32 lg:pt-36`). Padding here would double it.
 *  2. On the landing, section links stay native `<a href="#id">` anchors — the
 *     exact elements #636/#644 were tuned against. Only off-landing do they
 *     become router links. See SectionLink.
 */

// Header nav. Order mirrors the order the destinations appear to a visitor:
// Features is the standalone page, then the landing runs hero → #security →
// #pricing → #faq, which is why Security sits second and not last. Every
// *section* entry must resolve to an id that actually exists on the landing,
// and all three do. ("How it works" was once in this list; the section it
// pointed at was cut, so the link went with it. #features went the same way in
// BEN-141 item 9 — see below.)
//
// `show` is the breakpoint each link appears at — all four from md. Security
// (#142) is the fourth, and it did not fit for free: it costs ~36px with its
// divider where md had 30.6px spare, so 768 overflowed by 5px with the logo
// against the row. It was NOT solved by giving Security a later breakpoint —
// the hamburger is md:hidden, so an lg-only link would be unreachable between
// 768 and 1023, which is worse than tight. The 20px chevron and 8px of CTA
// padding became lg-only instead; see the CTA below.
//
// Below md the drawer carries every link instead — see MarketingMobileNav. The
// three that used to show from sm are hidden now: a partial inline set plus a
// hamburger is both redundant and, at 640–700, wider than the row can take. One
// affordance below md, the full inline row from md.
//
// "Features" routes to the standalone /features page (Phase 2). It pointed at
// the landing's #features band while that page was an empty stub, and carried a
// vestigial `href: "#features"` alongside its `to` long after the `to` won —
// dead as a link, but live as the React key below, which is why it survived.
// BEN-141 item 9 deleted that band, so the anchor now has no target at all.
//
// Hence the tagged union in lib/marketingNav — an entry is EITHER a route or a
// landing-section anchor, never both and never neither. See that file for why.
// AN-18 (2026-10-12): with Contact as a fifth link the inline row no longer fits at
// md. Measured at 768: the trial button ended at 781 (13px past the viewport); at
// 14px text it still ran 33px into the row's padding (761 against develop's 728).
// So the inline row starts at lg (1024, where it ends at 960 with 64px padding) and
// the drawer carries every link below lg: still one affordance per width.
const NAV_LINKS: MarketingNavLink[] = [
  { kind: "route", to: "/features", label: "Features", show: "hidden lg:inline" },
  // Doc 3 B2 (Anthony's package, 2026-10-28): "How it works" after Features. It opens the
  // walkthrough at step 1 (B3); the drawer carries it below lg.
  { kind: "action", action: "how-it-works", label: WALKTHROUGH_NAV_COPY, show: "hidden lg:inline" },
  { kind: "section", hash: "#security", label: "Security", show: "hidden lg:inline" },
  { kind: "section", hash: "#pricing", label: "Pricing", show: "hidden lg:inline" },
  { kind: "section", hash: "#faq", label: "FAQ", show: "hidden lg:inline" },
  // AN-18 (Anthony, 9 Oct call, asked twice): Contact in the top nav. A route, like
  // Features; the drawer carries it below md.
  { kind: "route", to: "/contact", label: "Contact", show: "hidden lg:inline" },
];

// The hairline between nav items. One definition, three placements (Features |
// Pricing | FAQ | Log in) — Anthony wanted the divider that already sat between
// FAQ and Log in repeated between every nav word, and three hand-written copies
// of it is exactly how the links themselves drifted before #636.
//
// `hidden lg:block` ties each divider to the links it separates: below lg the
// nav words are in the drawer, so a divider there would be a hairline floating
// between nothing and nothing.
//
// -mx-2 is load-bearing, not styling. As a plain flex child a divider claims the
// row's gap on BOTH sides — 12 + 1 + 12 = 25px where a bare gap between two words
// costs 12px, so each hairline would add 13px and four of them 52px to a row with
// 30.6px spare at md. Pulling 8px back per side leaves 9px between words at md
// and 17px from lg (where the row gap is 16px), and hands ~64px back.
// Weight, colour and height are the divider this replaces, untouched.
function NavDivider() {
  return (
    <div aria-hidden className="hidden h-6 w-px -mx-2 bg-neutral-300 lg:block dark:bg-border" />
  );
}

// Was three copy-pasted <a> tags (#636); consolidated so a fourth link
// couldn't drift from the other three.
function NavAnchor({ link }: { link: MarketingNavLink }) {
  const { label, show } = link;
  // 15px below lg, 16px from lg. Four links + Log in + a 193px CTA + the 189px
  // lockup is a dead heat at 768 — it needed one more pixel than the padding box
  // has. 15px is not a new size: it is what the footer links in this same file
  // already use, and it buys ~16px across the five text items.
  const className = cn(
    "whitespace-nowrap text-[15px] text-neutral-700 hover:text-neutral-900 lg:text-base",
    // The header flips to `dark:bg-background/85`; these did not flip with it,
    // so all five nav items sat at 1.30:1 in dark mode (#1219).
    "dark:text-muted-foreground dark:hover:text-foreground",
    show,
  );
  if (link.kind === "action") {
    return <HowItWorksButton className={className}>{label}</HowItWorksButton>;
  }
  if (link.kind === "route") {
    // AN-15: a route link to the page you're on doesn't remount it, so it wouldn't
    // scroll; same as the logo, it goes to the top.
    return (
      <Link to={link.to} onClick={() => window.scrollTo(0, 0)} className={className}>
        {label}
      </Link>
    );
  }
  return (
    <SectionLink hash={link.hash} className={className}>
      {label}
    </SectionLink>
  );
}

function MarketingHeader() {
  return (
    <header className="@container fixed top-0 left-0 right-0 z-20 pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] border-b border-neutral-200/60 bg-white/85 backdrop-blur-md dark:border-border dark:bg-background/85">
      {/* px-5 below sm: at 390 the wordmark + "Log in" + the CTA overflow px-10
          and collide. Horizontal padding only — py-6 feeds the header height
          that anchor jumps land against (#644), so leave it alone. */}
      {/* CALL-05 group 3 (2026-10-26): under 22rem of header (a 360 phone is 22.5rem
          at default text, 11.25rem at 200%) the row cannot hold logo + "Start 7-Day Free
          Trial" + menu; at 200% the button ran 217px past the screen on every public
          page. There the row wraps: logo and menu stay on the first line and the button
          takes a full-width line of its own, its label free to wrap. A container query,
          not a media query, because a media query's rem ignores the page's text size.
          The right-hand group dissolves (`contents`) so its button can be ordered
          last on the outer row. */}
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-6 sm:px-10 lg:px-16 @max-[22rem]:flex-wrap @max-[22rem]:gap-y-2 @max-[22rem]:py-2">
        {/* Icon-only below sm. The full lockup plus "Log in" and the CTA needs
            ~408px, so at 390 the wordmark overflows its flex-shrunk anchor and
            paints over "Log in". */}
        <Link
          to="/"
          aria-label="Fomo-Fi home"
          onClick={() => window.scrollTo(0, 0)}
          className="shrink-0"
        >
          <span className="sm:hidden">
            <FomoFiLogo size="lg" showText={false} />
          </span>
          <span className="hidden sm:block">
            <FomoFiLogo size="lg" />
          </span>
        </Link>
        {/* gap-3 below lg: the extra 4px per gap is what lets the fourth link
            fit from md, and it also un-sticks the logo from the first link at
            640, where they were touching. Full gap-4 returns at lg. */}
        <div className="flex items-center gap-3 lg:gap-4 @max-[22rem]:contents">
          {NAV_LINKS.map((link, i) => (
            <Fragment key={navKey(link)}>
              {i > 0 && <NavDivider />}
              <NavAnchor link={link} />
            </Fragment>
          ))}
          {/* Closes the run: Features | Security | Pricing | FAQ | Log in. This
              is the divider that already existed; it was sm:block, which painted
              a stray hairline next to the logo at 640–767 where the nav words are
              in the drawer. Now it keeps step with the others. */}
          <NavDivider />
          {/* Inline only from md, where the rest of the nav row lives. Below md
              the header carries logo + trial CTA + hamburger and the drawer
              carries Log in, so nothing becomes unreachable.
              Why it had to give: "Start 7-Day Free Trial" is 193px below sm
              against "Start 7-Day Trial"'s 156px, and 390 had 37px spare. The
              logo went to touching the row at 390 and 360 overflowed by 10px.
              Log in plus its gap is 57px — the only item on the row that is
              duplicated in the drawer, so it is the one that can stand down. */}
          <Link
            to="/auth/login"
            className="hidden whitespace-nowrap text-[15px] font-medium text-neutral-700 transition hover:text-neutral-900 lg:inline lg:text-base dark:text-muted-foreground dark:hover:text-foreground"
          >
            Log in
          </Link>
          {/* The row is tight in two separate places, for two separate reasons,
              and the same two decorative pixels pay for both.
              Below sm: "Start 7-Day Free Trial" is 193px where "Sign Up" was
              119px, which put the logo against the row at 390 and overflowed
              360 by 10px. From md: a fourth marketing link (Security, #142)
              costs ~36px and 768 only had 30.6px, so it overflowed by 5px.
              So the chevron and the wider padding are lg-only luxuries — they
              appear where the row has room and stand down where it doesn't,
              worth 20px and 8px. The LABEL is identical at every width; only
              the glyph and the padding are responsive. */}
          <Link
            to="/auth/signup"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg px-4 py-2.5 text-base font-semibold text-white shadow-sm transition hover:opacity-90 lg:px-5 @max-[22rem]:order-last @max-[22rem]:w-full @max-[22rem]:justify-center @max-[22rem]:whitespace-normal @max-[22rem]:py-2 @max-[22rem]:text-center @max-[22rem]:leading-tight"
            style={{ backgroundColor: HERO_BLUE }}
          >
            Start 7-Day Free Trial
            <ChevronRight className="hidden h-4 w-4 lg:block" strokeWidth={2.5} />
          </Link>
          {/* Below md this is the only route to the marketing links. */}
          <MarketingMobileNav
            links={NAV_LINKS}
            renderSectionLink={(props) => <SectionLink {...props} />}
          />
        </div>
      </div>
    </header>
  );
}

export default function MarketingLayout({ children }: { children: React.ReactNode }) {
  const { hash } = useLocation();

  // Same problem, same fix as LegalLayout (#634): React Router does no scroll
  // restoration, so an SPA hop between marketing pages kept the previous page's
  // offset — arriving at /features from deep in the landing opened it mid-page.
  // On mount: honour a section deep-link if the URL carries one (this is what
  // makes "/#pricing" from /features land), otherwise start at the top.
  //
  // scrollIntoView() respects the sections' scroll-margin, so the landing under
  // the 103px fixed header (#644) is identical to a native anchor jump.
  //
  // The re-assert is the Phase 1 bug: the first scroll landed correctly, then
  // ~150-300ms later the position slid ~29px. Total page height never changed,
  // so it wasn't image reflow — it was the browser restoring the scroll it had
  // remembered for that history entry, which lands after our programmatic one.
  // Turning restoration off stops it competing, and the rAF pass re-asserts
  // after the browser's own post-navigation scroll has had its go.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const target = hash ? document.getElementById(hash.slice(1)) : null;
    if (!target) {
      window.scrollTo(0, 0);
      return;
    }

    target.scrollIntoView();
    // Two frames out, then once more after the load event: covers the restore
    // above and any late webfont swap that reflows the text above the target.
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => target.scrollIntoView()));
    const onLoad = () => target.scrollIntoView();
    if (document.readyState !== "complete") {
      window.addEventListener("load", onLoad, { once: true });
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("load", onLoad);
    };
    // Mount-only by design: same-page section links are still native anchors,
    // and reacting to their hash changes would double-scroll the jump.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-white text-neutral-900 antialiased dark:bg-background dark:text-foreground">
      <MarketingHeader />
      {/* flex-1 so the footer sits at the bottom on pages shorter than the
          viewport. No-op on the landing, which is far taller. */}
      <main className="flex-1">{children}</main>
      <MarketingFooter />
      {/* The landing and /features are the longest pages in the product. */}
      <BackToTop />
    </div>
  );
}
